import { useMemo } from "react";
import { Box, Heading, Tooltip, Icon, HStack } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useSelector } from "react-redux";
import _ from "lodash";

import FilterForm from "../../components/FilterForm";
import Loader from "../../components/loader";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import ArrivalRatesPerHourReport from "../../components/organization/reports/ArrivalRatesPerHour";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import { normalizeArrivalRatesPerHourReports } from "../../store/arrivalRates";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import { ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS } from "../../store/renderConfig";
import { useReportPresentation } from "../../hooks/useReportPresentation";
import { downloadArrivalRatesSummaryCsvFiles } from "../../store/arrivalRatesSummaryExport";

const ArrivalRatesPerHour = ({ showFilterForm = true, showTitle = 1 }) => {
  const { loading, reportRange, reportsData, organizationId, hoursPerDay } = useSelector(
    (state) => state.defaultReducer
  );
  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_hour
      : REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_hour;
  const report = reportsData.find(
    (item) => item.reportTypeId === reportTypeId && item.organizationId === organizationId
  );
  const normalizedReport = normalizeArrivalRatesPerHourReports(report, hoursPerDay);
  const data = normalizedReport?.data || [];
  const {
    effectiveRenderConfig,
    isSaving,
    presentationSummaryItems,
    saveRenderConfig,
    setRenderConfig,
  } = useReportPresentation({
    reportTypeId,
    columnDefinitions: ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
  });
  const csvScopes = useMemo(
    () => [{ ...data, scopeName: "Entire Organization" }],
    [data]
  );

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>{getReportRangeString(reportRange)} Organization Arrival Rates Per Hour</title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : null}
      {loading ? <Loader /> : (
        <>
          {!_.isEmpty(data) ? (
            <>
              <HStack flexDirection="row" justifyContent="space-between">
                <Box>
                  <Heading fontSize="34px" color="#222" fontWeight="700">
                    Arrival Rates Per Hour
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                      </Tooltip.Trigger>
                      <Tooltip.Positioner>
                        <Tooltip.Content>
                          {getReportDescription(reportRange).ArrivalRatesPerHour}
                        </Tooltip.Content>
                      </Tooltip.Positioner>
                    </Tooltip.Root>
                  </Heading>
                </Box>
                <DownloadReport
                  reportRange={reportRange}
                  reportTypeId={reportTypeId}
                  renderConfig={effectiveRenderConfig}
                  onSave={saveRenderConfig}
                  isSaving={isSaving}
                  onDownloadCsv={() =>
                    downloadArrivalRatesSummaryCsvFiles({
                      scopes: csvScopes,
                      renderConfig: effectiveRenderConfig,
                      filenamePrefix: "organization_arrival_rates_per_hour",
                    })
                  }
                />
              </HStack>
              <ReportPresentationControls
                renderConfig={effectiveRenderConfig}
                onChange={setRenderConfig}
                columnDefinitions={ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS}
                defaultSortColumn="total"
              />
              <ReportPresentationSummary items={presentationSummaryItems} />
              <ArrivalRatesPerHourReport
                data={data}
                renderConfig={effectiveRenderConfig}
                onRenderConfigChange={setRenderConfig}
              />
            </>
          ) : (
            <SelectBranchInfo message="Update Filters and Hit Search to retrieve data" />
          )}
        </>
      )}
    </Box>
  );
};

export default ArrivalRatesPerHour;
